import {
  CoordinatorComment,
  CrimeCategory,
  CrimeReference,
  CrimeReferenceReceptionMethod,
  CrimeSubCategory,
  DirectorComment,
  Evidence,
  Exhibit,
  FurtherInvestigation,
  Investigation,
  PreliminaryInquiry,
  PreliminaryInquiryWitness,
  InvestigationFile,
  ScreeningFindings,
  ScreeningTeamMember,
  IncidentByMobile,
} from "types";
import apiClient from "../lib/apiClients";
import { CourtMarks, withCourtMarks } from "components/CourtFilingMarks";

export const fetchCrimeReferences = async (): Promise<CrimeReference[]> => {
  // The list endpoint is now paginated; request a large page and unwrap so the
  // existing "fetch-all-then-find" callers keep working.
  const response = await apiClient.get(
    "/crime-reference/crime-references/?page_size=1000",
  );
  return toPage<CrimeReference>(response.data).results;
};
export const fetchCrimeReferencesByCoordinatorRole = async (
  admin_unit_id: string | number,
  coordinator_role: string,
): Promise<CrimeReference[]> => {
  const response = await apiClient.get(
    `/crime-reference/crime-references/by-coordinator-role/?admin_unit_id=${admin_unit_id}&coordinator_role=${coordinator_role}`,
  );
  return toPage<CrimeReference>(response.data).results;
};

// --- Page-number paginated variants ({ count, next, previous, results }) ---
export interface PageResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

// Builds the query string: endpoint-specific params (extra) + pagination params.
const buildListQuery = (
  params?: ListParams,
  extra?: Record<string, string | number>,
) => {
  const q = new URLSearchParams();
  if (extra)
    Object.entries(extra).forEach(([k, v]) => q.set(k, String(v ?? "")));
  if (params?.page) q.set("page", String(params.page));
  if (params?.pageSize) q.set("page_size", String(params.pageSize));
  if (params?.search) q.set("search", params.search);
  if (params?.status) q.set("status", params.status);
  return q.toString();
};

// Normalizes either a plain array or a { count, ... } envelope into PageResult.
const toPage = <T>(data: any): PageResult<T> =>
  Array.isArray(data)
    ? { count: data.length, next: null, previous: null, results: data }
    : {
        count: data?.count ?? data?.results?.length ?? 0,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: data?.results ?? [],
      };

export const fetchCrimeReferencesPaginated = async (
  params?: ListParams,
): Promise<PageResult<CrimeReference>> => {
  const qs = buildListQuery(params);
  const response = await apiClient.get(
    `/crime-reference/crime-references/${qs ? `?${qs}` : ""}`,
  );
  return toPage<CrimeReference>(response.data);
};

export const fetchCrimeReferencesByCoordinatorRolePaginated = async (
  admin_unit_id: string | number,
  coordinator_role: string,
  params?: ListParams,
): Promise<PageResult<CrimeReference>> => {
  const qs = buildListQuery(params, { admin_unit_id, coordinator_role });
  const response = await apiClient.get(
    `/crime-reference/crime-references/by-coordinator-role/?${qs}`,
  );
  return toPage<CrimeReference>(response.data);
};
export const fetchFurtherInvestigationFromCRandIncidentRegNo = async (
  cr_number: number | string,
  incident_registration_number: number | string,
) => {
  const response = await apiClient.get<FurtherInvestigation[]>(
    `/crime-reference/crime-references/further-investigations/?cr_number=${cr_number}&incident_number=${incident_registration_number}`,
  );
  return response.data;
};
export const fetchInvestigationFileFromCRandIncidentRegNo = async (
  cr_number: number | string,
  incident_registration_number: number | string,
) => {
  const response = await apiClient.get<InvestigationFile[]>(
    `/crime-reference/crime-references/receiving-files/?cr_number=${cr_number}&incident_number=${incident_registration_number}`,
  );
  return response.data;
};
export const fetchCrimeReferencesById = async (uu_id: string | undefined) => {
  const response = await apiClient.get<CrimeReference>(
    "/crime-reference/crime-references/" + uu_id + "/",
  );
  return response.data;
};
export const fetchCrimeReferencesByCRNumberAndIncidentRegNo = async (
  crimeReferenceNumber: number | string,
  incidentRegistrationNumber: number | string,
) => {
  const response = await apiClient.get<CrimeReference[]>(
    `/prosecutor-police/investigation-files/filter-crime-reference/?crime_registration_number=${crimeReferenceNumber}&incident_registration_number=${incidentRegistrationNumber}`,
  );
  return response.data;
};
export const fetchCrimeReferenceReceptionMethod = async () => {
  const response = await apiClient.get<CrimeReferenceReceptionMethod[]>(
    "/crime-reference/reception-methods/",
  );
  return response.data;
};
export const addCrimeReferences = async (incident: CrimeReference) => {
  const response = await apiClient.post<CrimeReference>(
    "/crime-reference/crime-references/",
    {
      ...incident,
      reception_method_id: incident.reception_method,
      decision_type: "pending",
      screening_team: [],
    },
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const editCrimeReferences = async (uu_id: number, incident: any) => {
  const response = await apiClient.patch(
    `/crime-reference/crime-references/${uu_id}/`,
    incident,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const editCrimeReferences2 = async (
  uu_id: any,
  incident: any,
  marks?: CourtMarks,
) => {
  const { body, config } = withCourtMarks(incident, marks);
  const response = await apiClient.patch(
    `/crime-reference/crime-references/${uu_id}/`,
    body,
    config,
  );
  return response.data;
};

export const updateDecision = async (
  uu_id: any,
  decision_type: string,
  decision_date: Date,
) => {
  const response = await apiClient.patch(
    `/crime-reference/crime-references/${uu_id}/`,
    {
      decision_type,
      decision_date,
    },
  );
  return response.data;
};

// EVIDENCE
export const fetchEvidence = async () => {
  const response = await apiClient.get<Evidence[]>(
    "/crime-reference/evidences/",
  );
  return response.data;
};
export const fetchEvidenceByInvestigation = async (cr_number: string) => {
  const response = await apiClient.get<Evidence[]>(
    `/crime-reference/evidences/?cr_number=${cr_number}`,
  );
  return response.data;
};
export const fetchEvidenceByIncidentRegistrationNumber = async (
  incident_registration_number: string,
) => {
  const response = await apiClient.get<Evidence[]>(
    `/crime-reference/evidences/?incident_registration_number=${incident_registration_number}`,
  );
  return response.data;
};
export const fetchEvidenceByCrNumber = async (CR_number: string) => {
  const response = await apiClient.get<Evidence[]>(
    "/crime-reference/evidences/?cr_number=" + CR_number,
  );
  return response.data;
};
export const addEvidence = async (evidence: Evidence) => {
  const formData = new FormData();
  evidence?.file_attachments &&
    evidence?.file_attachments.forEach((file: any) => {
      formData.append("file_attachments", file);
    });

  // Add other fields from evidence object if needed
  Object.entries(evidence).forEach(([key, value]) => {
    if (key !== "file_attachments" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.post(
    "/crime-reference/evidences/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editEvidence = async (evidence: Evidence) => {
  const formData = new FormData();
  evidence?.file_attachments &&
    evidence?.file_attachments.forEach((file: any) => {
      formData.append("file_attachments", file);
    });

  // Add other fields from evidence object if needed
  Object.entries(evidence).forEach(([key, value]) => {
    if (key !== "file_attachments" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.put(
    "/crime-reference/evidences/" + evidence?.uu_id + "/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteEvidence = async (evidence: Evidence) => {
  const response = await apiClient.delete(
    "/crime-reference/evidences/" + evidence?.uu_id + "/",
  );
  return response.data;
};

// EXHIBIT
export const fetchExhibit = async () => {
  const response = await apiClient.get<Exhibit[]>("/crime-reference/exhibits/");
  return response.data;
};
export const fetchExhibitByCrNumber = async (CR_number: string) => {
  const response = await apiClient.get<Exhibit[]>(
    "/crime-reference/exhibits/?cr_number=" + CR_number,
  );
  return response.data;
};
export const fetchExhibitByIncidentRegistrationNumber = async (
  Incident_Registration_number: string,
) => {
  const response = await apiClient.get<Exhibit[]>(
    "/crime-reference/exhibits/?Incident_Registration_number=" +
      Incident_Registration_number,
  );
  return response.data;
};
export const addExhibit = async (exhibit: Exhibit) => {
  const formData = new FormData();
  exhibit?.exhibit_attachment_files &&
    exhibit?.exhibit_attachment_files.forEach((file: any) => {
      formData.append("exhibit_attachment_files", file);
    });

  // Add other fields from exhibit object if needed
  Object.entries(exhibit).forEach(([key, value]) => {
    if (key !== "exhibit_attachment_files" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.post(
    "/crime-reference/exhibits/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editExhibit = async (exhibit: Exhibit) => {
  const formData = new FormData();
  exhibit?.exhibit_attachment_files &&
    exhibit?.exhibit_attachment_files.forEach((file: any) => {
      formData.append("exhibit_attachment_files", file);
    });

  exhibit?.prosecution_files &&
    formData.append("prosecution_files", exhibit?.prosecution_files);

  exhibit?.court_files && formData.append("court_files", exhibit?.court_files);

  // Add other fields from exhibit object if needed
  Object.entries(exhibit).forEach(([key, value]) => {
    if (
      key !== "exhibit_attachment_files" &&
      key !== "prosecution_files" &&
      key !== "court_files" &&
      value !== undefined
    ) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.patch(
    "/crime-reference/exhibits/" + exhibit?.uu_id + "/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editExhibit2 = async (exhibit: any) => {
  const response = await apiClient.patch(
    "/crime-reference/exhibits/" + exhibit?.uu_id + "/",
    exhibit,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteExhibit = async (exhibit: Exhibit) => {
  const response = await apiClient.delete(
    "/crime-reference/exhibits/" + exhibit?.uu_id + "/",
  );
  return response.data;
};
// CRIME_CATEGORY
export const fetchCrimeCategory = async () => {
  const response = await apiClient.get<CrimeCategory[]>(
    "/crime-reference/categories/",
  );
  return response.data;
};
export const addCrimeCategory = async (exhibit: CrimeCategory) => {
  const response = await apiClient.post(
    "/crime-reference/categories/",
    exhibit,
  );
  return response.data;
};
export const editCrimeCategory = async (exhibit: CrimeCategory) => {
  const response = await apiClient.put(
    "/crime-reference/categories/" + exhibit?.uu_id + "/",
    exhibit,
  );
  return response.data;
};
export const deleteCrimeCategory = async (exhibit: CrimeCategory) => {
  const response = await apiClient.delete(
    "/crime-reference/categories/" + exhibit?.uu_id + "/",
  );
  return response.data;
};

// CRIME_SUB_CATEGORY
export const fetchCrimeSubCategory = async () => {
  const response = await apiClient.get<CrimeSubCategory[]>(
    "/crime-reference/subcategories/",
  );
  return response.data;
};
export const addCrimeSubCategory = async (exhibit: CrimeSubCategory) => {
  const response = await apiClient.post(
    "/crime-reference/subcategories/",
    exhibit,
  );
  return response.data;
};
export const editCrimeSubCategory = async (exhibit: CrimeSubCategory) => {
  const response = await apiClient.put(
    "/crime-reference/subcategories/" + exhibit?.uu_id + "/",
    exhibit,
  );
  return response.data;
};
export const deleteCrimeSubCategory = async (exhibit: CrimeSubCategory) => {
  const response = await apiClient.delete(
    "/crime-reference/subcategories/" + exhibit?.uu_id + "/",
  );
  return response.data;
};

// CRIME_SUB_CATEGORY
export const fetchInvestigation = async () => {
  const response = await apiClient.get<Investigation[]>(
    "/crime-reference/investigations/",
  );
  return response.data;
};
export const addInvestigation = async (exhibit: Investigation) => {
  const response = await apiClient.post(
    "/crime-reference/investigations/",
    exhibit,
  );
  return response.data;
};
export const addInvestigation2 = async (exhibit: any) => {
  const response = await apiClient.post(
    "/crime-reference/investigations/",
    exhibit,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editInvestigation = async (investigation: any) => {
  const response = await apiClient.patch(
    "/crime-reference/investigations/" + investigation?.uu_id + "/",
    investigation,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteInvestigation = async (exhibit: Investigation) => {
  const response = await apiClient.delete(
    "/crime-reference/investigations/" + exhibit?.uu_id + "/",
  );
  return response.data;
};

// DIRECTOR_COMMENT
export const fetchDirectorComment = async () => {
  const response = await apiClient.get<DirectorComment[]>(
    "/crime-reference/director-comments/",
  );
  return response.data;
};
export const addDirectorComment = async (comment: DirectorComment) => {
  const response = await apiClient.post(
    "/crime-reference/director-comments/",
    comment,
  );
  return response.data;
};
export const editDirectorComment = async (comment: DirectorComment) => {
  const response = await apiClient.put(
    "/crime-reference/director-comments/" + comment?.uu_id + "/",
    comment,
  );
  return response.data;
};
export const deleteDirectorComment = async (comment: DirectorComment) => {
  const response = await apiClient.delete(
    "/crime-reference/director-comments/" + comment?.uu_id + "/",
  );
  return response.data;
};

// COORDINATOR_COMMENT
export const fetchCoordinatorComment = async () => {
  const response = await apiClient.get<CoordinatorComment[]>(
    "/crime-reference/coordinator-comments/",
  );
  return response.data;
};
export const addCoordinatorComment = async (comment: CoordinatorComment) => {
  const response = await apiClient.post(
    "/crime-reference/coordinator-comments/",
    comment,
  );
  return response.data;
};
export const editCoordinatorComment = async (comment: CoordinatorComment) => {
  const response = await apiClient.put(
    "/crime-reference/coordinator-comments/" + comment?.uu_id + "/",
    comment,
  );
  return response.data;
};
export const deleteCoordinatorComment = async (comment: CoordinatorComment) => {
  const response = await apiClient.delete(
    "/crime-reference/coordinator-comments/" + comment?.uu_id + "/",
  );
  return response.data;
};

// PRELIMINARY_INQUIRY
export const fetchPreliminaryInquiry = async () => {
  const response = await apiClient.get<PreliminaryInquiry[]>(
    "/crime-reference/preliminary-inquiries/",
  );
  return response.data;
};
export const fetchPreliminaryInquiryByWitness = async (uu_id: number) => {
  const response = await apiClient.get<PreliminaryInquiry>(
    `/crime-reference/preliminary-inquiries/by-witness/${uu_id}`,
  );
  return response.data;
};
export const addPreliminaryInquiry = async (data: PreliminaryInquiry) => {
  const response = await apiClient.post(
    "/crime-reference/preliminary-inquiries/",
    data,
  );
  return response.data;
};
export const editPreliminaryInquiry = async (data: PreliminaryInquiry) => {
  const response = await apiClient.put(
    "/crime-reference/preliminary-inquiries/" + data?.uu_id + "/",
    data,
  );
  return response.data;
};
export const deletePreliminaryInquiry = async (data: PreliminaryInquiry) => {
  const response = await apiClient.delete(
    "/crime-reference/preliminary-inquiries/" + data?.uu_id + "/",
  );
  return response.data;
};

// PRELIMINARY_INQUIRY-WITNESS
export const fetchPreliminaryInquiryWitness = async () => {
  const response = await apiClient.get<PreliminaryInquiryWitness[]>(
    "/crime-reference/preliminary-inquiry-witnesses/",
  );
  return response.data;
};
export const addPreliminaryInquiryWitness = async (
  data: PreliminaryInquiryWitness,
) => {
  const response = await apiClient.post(
    "/crime-reference/preliminary-inquiry-witnesses/",
    data,
  );
  return response.data;
};
export const editPreliminaryInquiryWitness = async (
  data: PreliminaryInquiryWitness,
) => {
  const response = await apiClient.put(
    "/crime-reference/preliminary-inquiry-witnesses/" + data?.uu_id + "/",
    data,
  );
  return response.data;
};
export const deletePreliminaryInquiryWitness = async (
  data: PreliminaryInquiryWitness,
) => {
  const response = await apiClient.delete(
    "/crime-reference/preliminary-inquiry-witnesses/" + data?.uu_id + "/",
  );
  return response.data;
};

// Screening Team Finding
export const fetchScreeningFinding = async () => {
  const response = await apiClient.get<ScreeningFindings[]>(
    "/crime-reference/screening-findings/",
  );
  return response.data;
};
export const addScreeningFinding = async (data: ScreeningFindings) => {
  const response = await apiClient.post(
    "/crime-reference/screening-findings/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editScreeningFinding = async (data: ScreeningFindings) => {
  const response = await apiClient.put(
    "/crime-reference/screening-findings/" + data?.uu_id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteScreeningFinding = async (data: ScreeningFindings) => {
  const response = await apiClient.delete(
    "/crime-reference/screening-findings/" + data?.uu_id + "/",
  );
  return response.data;
};

// Screening Team Members
export const fetchScreeningTeamMembers = async () => {
  const response = await apiClient.get<ScreeningTeamMember[]>(
    "/crime-reference/screening-team-members/",
  );
  return response.data;
};
export const addScreeningTeamMembers = async (data: ScreeningTeamMember) => {
  const response = await apiClient.post(
    "/crime-reference/screening-team-members/",
    data,
  );
  return response.data;
};
export const editScreeningTeamMembers = async (data: ScreeningTeamMember) => {
  const response = await apiClient.put(
    "/crime-reference/screening-team-members/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const deleteScreeningTeamMembers = async (data: ScreeningTeamMember) => {
  const response = await apiClient.delete(
    "/crime-reference/screening-team-members/" + data?.id + "/",
  );
  return response.data;
};
// ============================
export const fetchIncidentByMobile = async () => {
  const response = await apiClient.get<IncidentByMobile[]>(
    "/base_data/eco-cor-incident-reception-by-mobile/",
  );
  return response.data;
};

export const fetchIncidentByMobileById = async (id: string) => {
  const response = await apiClient.get<IncidentByMobile>(
    `/base_data/eco-cor-incident-reception-by-mobile/${id}/`,
  );
  return response.data;
};

export const updateIncidentStatus = async (id: string, status: string) => {
  const response = await apiClient.patch<IncidentByMobile>(
    `/base_data/eco-cor-incident-reception-by-mobile/${id}/`,
    { status },
  );
  return response.data;
};
